import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PartnerAPIService } from 'src/app/extensions/services/api/partner-api.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { AssetListContractComponent } from './asset-list-contract/asset-list-contract.component';
import { RESPONSIVE_BREAKPOINTS } from 'src/app/extensions/constants/responsive-breakpoints';
import { WindowEventsService } from 'src/app/extensions/services/window-events.services';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { scrollTop, setTableViewMode } from 'src/app/extensions/utilities';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DEFAULT_DEBOUNCE_TIME } from 'src/app/extensions/constants/commonConstants';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { AssetAPIService } from 'src/app/extensions/services/api/asset-api.service';
import { DictionaryAssetDeliveryStatus } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { BannerNotificationService } from 'src/app/extensions/services/banner-notification.service';

@Component({
  selector: 'app-asset-list',
  templateUrl: './asset-list.component.html',
  styleUrls: ['./asset-list.component.scss'],
  providers: [ScrollService],
  standalone: false,
})
export class AssetListComponent implements OnInit, OnDestroy {
  @ViewChild('assetSearch') public assetSearchControl: TextBoxComponent;

  public assets: app.models.AssetListModel[] = [];
  public orderedAssets: app.models.AssetListModel[] = [];

  public props: app.models.DynamicPropsDictionary;
  public subscriptions: Subscription[] = [];
  private pageIndex = 1;
  private maxIndex: number;
  public activeFilterCount = 0;
  public hasData = false;
  public loading: boolean = true;
  public showBackToTop: boolean = false;

  public currentRoute: string;
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;

  public pageFilter: app.models.GetPagedAssetsPageFilterModel;
  public initialFilterState: app.models.GetPagedAssetsPageFilterModel;

  public showActiveContracts: boolean = true;
  windowSizeLgUp: boolean;
  searchQuery$ = new Subject<string>();

  public client: app.models.Partner;
  warningMessages: string[] = [];

  AssetDeliveryStatus = DictionaryAssetDeliveryStatus;

  public constructor(
    private _globalService: GlobalService,
    private _router: Router,
    private _scrollService: ScrollService,
    private _assetAPIService: AssetAPIService,
    private _ngbModal: NgbModal,
    private _partnerAPIService: PartnerAPIService,
    private _activatedRoute: ActivatedRoute,
    private _windowEventService: WindowEventsService,
    private _bannerService: BannerNotificationService
  ) {
    this.currentRoute = this._router.url.split('/')[1];
  }

  public ngOnInit(): void {
    this.tableViewMode = setTableViewMode(this._globalService, this.currentRoute);

    forkJoin([
      this._partnerAPIService.getClient(),
      this._partnerAPIService.getPartnerRequestCreationRestrictions(),
    ]).subscribe(([client, requestCreationRestrictions]) => {
      this.client = client;

      if (requestCreationRestrictions.isRestricted) {
        this.warningMessages.push('requests.cards.contactSupportWarning');
      }
      if (requestCreationRestrictions.hasUnpaidInvoices) {
        this.warningMessages.push('requests.cards.unpaidInvoicesWarning');
      }
    });

    this.pageFilter = {
      contractIds: [],
      contractTypeCodes: [],
      searchText: '',
      activeContracts: true,
    };
    this.initialFilterState = structuredClone(this.pageFilter);

    const contractId = this._activatedRoute.snapshot.queryParams['contractId'];
    if (contractId != null) {
      this.pageFilter.contractIds.push(contractId);
    }

    this.loadData();

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._assetAPIService.getAssets(this.pageIndex, this.pageFilter).subscribe(x => {
          this.assets = this.assets.concat(x.data.values);

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    let resizeSubscription = this._windowEventService.onResize.subscribe(() => {
      this.windowSizeLgUp = window.innerWidth >= RESPONSIVE_BREAKPOINTS.lg;
    });
    this.subscriptions.push(resizeSubscription);

    let searchSubscription = this.searchQuery$
      .pipe(debounceTime(DEFAULT_DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(res => {
        this.pageIndex = 1;
        this.assetSearchControl.blur();
        this.pageFilter.searchText = res;
        this.initialFilterState.searchText = res;
        this.loadData(true);
      });
    this.subscriptions.push(searchSubscription);
  }

  showMobileStoreBanner() {
    this._bannerService.showBannerNotification({
      icon: 'icon-phone-02',
      translationString: 'asset.inspectionNotification',
      isVisible: true,
      appStoreLink: null,
      googlePlayLink: null,
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }

    this._bannerService.hideBannerNotification();
  }

  loadData(forcedFocusSearchBar: boolean = false) {
    this.loading = true;

    forkJoin({
      assetsResult: this._assetAPIService.getAssets(this.pageIndex, this.pageFilter),
      orderedAssetsResult: this._assetAPIService.getOrderedAssets(this.pageFilter),
    }).subscribe(({ assetsResult, orderedAssetsResult }) => {
      this.assets = assetsResult.data.values;
      this.props = assetsResult.props;
      this.maxIndex = assetsResult.data.totalPages;

      this.orderedAssets = orderedAssetsResult;

      this.hasData = this.assets.length > 0 || this.orderedAssets.length > 0;

      this.showActiveContracts = this.pageFilter.activeContracts;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;

      if (forcedFocusSearchBar) {
        this.assetSearchControl.focus();
      }

      if (this.assets.some(a => a.nextInspectionDate != null)) {
        this.showMobileStoreBanner();
      }

      this.loading = false;
    });
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  toggleAssetsListByContractState() {
    this.pageFilter = {
      contractIds: [],
      contractTypeCodes: [],
      searchText: '',
      activeContracts: !this.pageFilter.activeContracts,
    };
    this.initialFilterState = structuredClone(this.pageFilter);

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.loadData();
  }

  resetFiltersHandler() {
    this.pageFilter = {
      contractIds: [],
      contractTypeCodes: [],
      searchText: '',
      activeContracts: true,
    };
    this.initialFilterState = structuredClone(this.pageFilter);

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.loadData();
  }

  public contractHandler() {
    let instance = this._ngbModal.open(AssetListContractComponent, {
      scrollable: true,
    });
    instance.componentInstance.pageFilter = this.pageFilter;
    instance.componentInstance.initialFilterState = this.initialFilterState;

    instance.closed.subscribe(response => {
      if (response !== false) {
        this.initialFilterState = structuredClone(this.pageFilter);
        this.pageIndex = 1;
        this.activeFilterCount = response;

        this._assetAPIService.getAssets(this.pageIndex, this.pageFilter).subscribe(x => {
          this.assets = x.data.values;
          this.props = x.props;
          this.maxIndex = x.data.totalPages;
          this.hasData = x.data.totalCount > 0;

          this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;
        });
      }
    });
  }

  scrollToTop(): void {
    scrollTop();
  }

  navigateToDetailHandler(assetId: string) {
    this._router.navigate(['asset-list', assetId]);
  }
}
